import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_CAROUSEL } from "@/data";

const AUTOPLAY_MS = 3800;

// Map each carousel story to its matching module card id (Modules.jsx)
const MODULE_IDS = {
  "Next-Gen AI Innovators": "ai-innovators",
  "Digital Artisans": "digital-artisans",
  "Culinary Entrepreneurship": "culinary",
  "Smart Agriculture": "smart-agri",
  "Advanced Dropshipping Mastery": "dropshipping",
  "Digital Marketing Pro": "digital-marketing",
};

export default function HeroCarousel({ imgY, imgScale }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = HERO_CAROUSEL.length;

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + count) % count),
    [count]
  );

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, count]);

  const slide = HERO_CAROUSEL[index];

  return (
    <div
      data-testid="hero-carousel"
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-emerald-green/20 to-vibrant-orange/20 blur-2xl" />
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="relative overflow-hidden rounded-[2rem] border border-white/60 shadow-2xl shadow-deep-trust-blue/20"
      >
        <div className="relative h-[420px] w-full md:h-[520px]">
          <AnimatePresence mode="popLayout">
            <motion.img
              key={index}
              src={slide.image}
              alt={`${slide.name}, ${slide.age} — ${slide.module}`}
              loading={index === 0 ? "eager" : "lazy"}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1.16 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: (AUTOPLAY_MS + 1400) / 1000, ease: "linear" },
              }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
        </div>

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-deep-trust-blue/70 via-deep-trust-blue/10 to-transparent" />

        {/* Module tag */}
        <div className="absolute left-5 top-5 z-20">
          <span
            data-testid="hero-carousel-tag"
            className="rounded-full bg-vibrant-orange px-3 py-1 text-xs font-semibold text-white shadow-lg"
          >
            {slide.module}
          </span>
        </div>

        {/* Caption card */}
        <div className="absolute bottom-5 left-5 right-5 z-20">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-white/20 bg-deep-trust-blue/70 p-4 backdrop-blur-md"
            >
              <p className="font-heading text-sm font-semibold text-white">
                {slide.name}, {slide.age} · {slide.location}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-white/75">{slide.story}</p>
              <a
                href={`#${MODULE_IDS[slide.module] || "modules"}`}
                data-testid="hero-carousel-module-link"
                className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-vibrant-orange transition-colors hover:text-orange-400"
              >
                Explore {slide.module}
                <ArrowUpRight size={13} />
              </a>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Arrows */}
        <button
          data-testid="hero-carousel-prev"
          aria-label="Previous story"
          onClick={() => go(-1)}
          className="absolute left-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-deep-trust-blue shadow-md transition-all duration-300 hover:bg-white hover:scale-110"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          data-testid="hero-carousel-next"
          aria-label="Next story"
          onClick={() => go(1)}
          className="absolute right-3 top-1/2 z-30 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/85 text-deep-trust-blue shadow-md transition-all duration-300 hover:bg-white hover:scale-110"
        >
          <ChevronRight size={20} />
        </button>
      </motion.div>

      {/* Dots */}
      <div className="mt-5 flex items-center justify-center gap-2">
        {HERO_CAROUSEL.map((s, i) => (
          <button
            key={i}
            data-testid={`hero-carousel-dot-${i}`}
            aria-label={`Go to ${s.name}'s story`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-vibrant-orange" : "w-2 bg-deep-trust-blue/20 hover:bg-deep-trust-blue/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
